/**
 * TanStack Query hooks for the multi-agent system.
 * All endpoints: backend/routers/agents.py
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import type {
  AgentConfigUpdate,
  AgentsOverview,
  ConfluenceResponse,
  EventBusStats,
  EventsResponse,
  LeaderboardResponse,
  PendingSignalsResponse,
  PortfolioRisk,
  PositionsResponse,
  RiskVerdictsResponse,
  TradeHistoryResponse,
} from "@/sdk/types/agents";

// ── Query Keys ──────────────────────────────────────────────────────────

export const AGENT_QUERY_KEYS = {
  all: ["agents"] as const,
  overview: ["agents", "overview"] as const,
  leaderboard: ["agents", "leaderboard"] as const,
  portfolio: ["agents", "portfolio"] as const,
  events: (agentId?: string) =>
    agentId
      ? (["agents", "events", agentId] as const)
      : (["agents", "events"] as const),
  confluence: ["agents", "confluence"] as const,
  busStats: ["agents", "bus-stats"] as const,
  agent: (id: string) => ["agents", id] as const,
  positions: (id: string, status: string) =>
    ["agents", id, "positions", status] as const,
  history: (id: string) => ["agents", id, "history"] as const,
  riskVerdicts: (id: string) => ["agents", id, "risk-verdicts"] as const,
  pendingSignals: ["agents", "signals", "pending"] as const,
};

// ── Overview & Aggregate ────────────────────────────────────────────────

export function useAgentsOverview(refetchInterval = 10_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.overview,
    queryFn: () => apiGet<AgentsOverview>("/api/agents"),
    refetchInterval,
  });
}

export function useAgentLeaderboard(refetchInterval = 30_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.leaderboard,
    queryFn: () => apiGet<LeaderboardResponse>("/api/agents/leaderboard"),
    refetchInterval,
  });
}

export function useAgentPortfolio(refetchInterval = 15_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.portfolio,
    queryFn: () => apiGet<PortfolioRisk>("/api/agents/portfolio"),
    refetchInterval,
  });
}

export function useAgentEvents(
  limit = 50,
  agentId?: string,
  refetchInterval = 5_000,
) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (agentId) params.set("agent_id", agentId);
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.events(agentId),
    queryFn: () =>
      apiGet<EventsResponse>(`/api/agents/events?${params.toString()}`),
    refetchInterval,
  });
}

export function useAgentConfluence(limit = 20, refetchInterval = 30_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.confluence,
    queryFn: () =>
      apiGet<ConfluenceResponse>(`/api/agents/confluence?limit=${limit}`),
    refetchInterval,
  });
}

export function useEventBusStats(refetchInterval = 10_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.busStats,
    queryFn: () => apiGet<EventBusStats>("/api/agents/bus-stats"),
    refetchInterval,
  });
}

// ── Single Agent ────────────────────────────────────────────────────────

export function useAgent(agentId: string, refetchInterval = 10_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.agent(agentId),
    queryFn: () => apiGet<Record<string, unknown>>(`/api/agents/${agentId}`),
    refetchInterval,
    enabled: !!agentId,
  });
}

export function useAgentPositions(
  agentId: string,
  status = "open",
  refetchInterval = 10_000,
) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.positions(agentId, status),
    queryFn: () =>
      apiGet<PositionsResponse>(
        `/api/agents/${agentId}/positions?status=${status}`,
      ),
    refetchInterval,
    enabled: !!agentId,
  });
}

export function useAgentHistory(
  agentId: string,
  limit = 50,
  refetchInterval = 30_000,
) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.history(agentId),
    queryFn: () =>
      apiGet<TradeHistoryResponse>(
        `/api/agents/${agentId}/history?limit=${limit}`,
      ),
    refetchInterval,
    enabled: !!agentId,
  });
}

export function useAgentRiskVerdicts(
  agentId: string,
  limit = 20,
  approvedOnly?: boolean,
  refetchInterval = 15_000,
) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (approvedOnly !== undefined)
    params.set("approved_only", String(approvedOnly));
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.riskVerdicts(agentId),
    queryFn: () =>
      apiGet<RiskVerdictsResponse>(
        `/api/agents/${agentId}/risk-verdicts?${params.toString()}`,
      ),
    refetchInterval,
    enabled: !!agentId,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useStartAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      apiPost<{ status: string; agent_id: string }>(
        `/api/agents/${agentId}/start`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.all });
    },
  });
}

export function useStopAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      apiPost<{ status: string; agent_id: string }>(
        `/api/agents/${agentId}/stop`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.all });
    },
  });
}

export function useUpdateAgentConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      config,
    }: {
      agentId: string;
      config: AgentConfigUpdate;
    }) =>
      apiPost<{ status: string; agent_id: string; updates: AgentConfigUpdate }>(
        `/api/agents/${agentId}/config`,
        config as unknown as Record<string, unknown>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.all });
    },
  });
}

// ── Pending Signals (approval mode) ─────────────────────────────────────

export function usePendingSignals(refetchInterval = 5_000) {
  return useQuery({
    queryKey: AGENT_QUERY_KEYS.pendingSignals,
    queryFn: () =>
      apiGet<PendingSignalsResponse>("/api/agents/signals/pending"),
    refetchInterval,
  });
}

export function useApproveSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      apiPost<{ status: string }>(`/api/agents/signals/${signalId}/approve`),
    // Invalidate on settle, not just success: a 404 "already resolved" must
    // also clear the stale card immediately instead of lingering until the
    // next poll.
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: AGENT_QUERY_KEYS.pendingSignals,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.all });
    },
  });
}

export function useRejectSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      apiPost<{ status: string }>(`/api/agents/signals/${signalId}/reject`),
    // onSettled (not onSuccess) so a failed reject of an already-resolved
    // signal still refreshes the pending list right away.
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: AGENT_QUERY_KEYS.pendingSignals,
      });
    },
  });
}

// ── Kill Switch (emergency trading halt + stop all agents) ───────────────

export function useKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ status: string }>("/api/agents/kill"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_QUERY_KEYS.all });
      // Refresh the live halt readout immediately rather than waiting for the
      // poll interval — a kill is a safety action the operator must see land.
      void qc.invalidateQueries({ queryKey: ["trading", "kill-switch"] });
    },
  });
}
